import {useParams} from 'react-router-dom'
import React, {useEffect, useRef, useState} from 'react'
import {RepromaterijalPromet} from '../../../models/RepromaterijaliPromet'
import {RolnaDetaljiProizvodnja} from '../../../models/FmkNaloziModel'
import {direktanIzlazRolne, fetchRepromaterijalPromet} from '../../../api/prometRepromaterijala'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import notifyToast from '../../../components/Toast/Toast'
import useDebounce from '../../../hooks/useDebounce'
import {rolnaDetaljiProizvodnja} from '../../../api/fmkNalozi'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../../components/TitleComponent/TitleComponent'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import QrCodeScanner from '../../../components/QrCodeScanner/QrCodeScanner'
import clsx from 'clsx'

/** Zebra + general cleanup */
const normalizeScan = (value: string) =>
    String(value ?? '')
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/** Robust company match: "Alternativa doo", "Alternativa doo.", "ALTERNATIVA   DOO" etc. */
const isCompanyAlternativaDoo = (s: string) => {
    const cleaned = normalizeScan(s)
        .toLowerCase()
        .replace(/[^a-z]/g, '')
    return cleaned === 'alternativadoo'
}

/**
 * ✅ Full rolna QR payload:
 * Accepts: "77927; ...; ...; ...; Alternativa doo" (+ optional whitespace / dot / trailing ';')
 */
const isFullRolnaQr = (raw: string) => {
    const s = normalizeScan(raw)
    if (!s) return false

    const parts = s
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)

    if (parts.length < 5) return false

    const last = parts[parts.length - 1] ?? ''
    return isCompanyAlternativaDoo(last)
}

/** If scanner provides only ID -> treat as scanned (when it came from device) */
const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw))

const extractBrojRolneFromQr = (raw: string): string => {
    const first = String(raw || '').split(';')[0] ?? ''
    return first.trim()
}

// ✅ same logic as KrajRolne: scanned = deviceScan && (idOnly || fullQR)
const computeSkenirano = (scannedByDevice: number, rawScan: string): 0 | 1 => {
    if (Number(scannedByDevice) !== 1) return 0
    const cleaned = normalizeScan(rawScan)
    return isIdOnly(cleaned) || isFullRolnaQr(cleaned) ? 1 : 0
}

type FormValues = Partial<RepromaterijalPromet> & {
    Repromaterijal: string
    BrojDokumentaIzlaza: string
    Napomene?: string
    // ✅ NEW: send to API
    Skenirano?: 0 | 1
    RawScan?: string
    ScannedByDevice?: 0 | 1
}

const PrometRolneDirektnaProdaja: React.FC = () => {
    const {id} = useParams()
    const idFromURL = Number(id)

    const initialValues: FormValues = {
        Repromaterijal: '',
        BrojDokumentaIzlaza: '',
        Napomene: '',
        Skenirano: 0,
        RawScan: '',
        ScannedByDevice: 0,
    }

    const [loading, setLoading] = useState(true)
    const [initialData, setInitialData] = useState<FormValues>(initialValues)

    const [rolnaDetaljiInfo, setrolnaDetalji] = useState<RolnaDetaljiProizvodnja | any | undefined>(undefined)
    const [rolnaLoading, setRolnaLoading] = useState(false)
    const [rolnaError, setRolnaError] = useState('')

    // request guard (avoid stale details showing)
    const reqIdRef = useRef(0)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (idFromURL) {
            fetchRepromaterijalPromet(idFromURL)
                .then((data) => {
                    const merged: FormValues = {
                        ...initialValues,
                        ...(data as any),
                        // ensure new fields exist even when editing old records
                        Skenirano: (data as any)?.Skenirano === 1 ? 1 : 0,
                        RawScan: (data as any)?.RawScan ?? '',
                        ScannedByDevice: (data as any)?.ScannedByDevice === 1 ? 1 : 0,
                    }
                    setInitialData(merged)
                    formik.resetForm({values: merged})
                })
                .finally(() => setLoading(false))
        } else {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idFromURL])

    const validationSchema = Yup.object({
        BrojDokumentaIzlaza: Yup.string()
            .matches(
                /^\d{6}\/\d{2}$/,
                'Format mora biti 6 cifara, kosa crta, godina (npr. 000005/26)'
            )
            .required('Broj naloga je obavezan'),
        Repromaterijal: Yup.string().required('Required'),
    })

    const formik = useFormik<FormValues>({
        initialValues: initialData,
        validationSchema,
        enableReinitialize: false, // we resetForm manually after fetch
        onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
            setLoading(true)
            try {
                // ✅ ensure skenirano is correct at submit time
                const finalSkenirano = computeSkenirano(values.ScannedByDevice || 0, values.RawScan || '')

                const payload: FormValues = {
                    ...values,
                    Skenirano: finalSkenirano,
                }

                const response = await direktanIzlazRolne(payload)
                const responseData = response

                if (responseData.error === 0) {
                    notifyToast(true, responseData.message)
                    resetForm({
                        values: {
                            ...initialValues,
                            BrojDokumentaIzlaza: ''
                        },
                    })
                    setrolnaDetalji(undefined)
                    setRolnaError('')
                } else {
                    notifyToast(false, responseData.message || 'An unexpected error occurred.')
                }
            } catch (error: any) {
                console.error('Error: ', error)
                notifyToast(false, error.response?.data.message || 'An error occurred')
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    // --- Rolna details: clear old info immediately on change and ignore stale requests
    const debouncedRolna = useDebounce(formik.values.Repromaterijal)

    useEffect(() => {
        const rolnaId = normalizeScan(debouncedRolna || '')

        if (!rolnaId) {
            setrolnaDetalji(undefined)
            setRolnaLoading(false)
            setRolnaError('')
            return
        }

        // ✅ clear old immediately so UI doesn't show stale "validna"
        setrolnaDetalji(undefined)
        setRolnaError('')
        setRolnaLoading(true)

        const myReqId = ++reqIdRef.current

        rolnaDetaljiProizvodnja({rolnaId})
            .then((response) => {
                if (myReqId !== reqIdRef.current) return
                const details = response?.data ?? response
                setrolnaDetalji(details)
            })
            .catch((error) => {
                if (myReqId !== reqIdRef.current) return
                const msg = error?.response?.data?.message || error?.message || 'Greška pri dohvatu detalja rolne'
                setRolnaError(msg)
                setrolnaDetalji(undefined)
            })
            .finally(() => {
                if (myReqId !== reqIdRef.current) return
                setRolnaLoading(false)
            })
    }, [debouncedRolna])

    // --- Scan handlers
    const applyDeviceScan = (raw: string) => {
        const cleaned = normalizeScan(raw)
        if (!cleaned) return

        const broj = extractBrojRolneFromQr(cleaned)

        // ✅ tracking fields (persisted + sent to backend)
        formik.setFieldValue('RawScan', cleaned)
        formik.setFieldValue('ScannedByDevice', 1)

        const sk = computeSkenirano(1, cleaned)
        formik.setFieldValue('Skenirano', sk)

        // ✅ set the actual rolna id field
        formik.setFieldValue('Repromaterijal', broj)

        // focus input (optional)
        setTimeout(() => inputRef.current?.focus(), 30)
    }

    const applyManualRolnaChange = (value: string) => {
        formik.setFieldValue('Repromaterijal', value)

        // ✅ manual typing => not scanned
        formik.setFieldValue('RawScan', '')
        formik.setFieldValue('ScannedByDevice', 0)
        formik.setFieldValue('Skenirano', 0)
    }

    const hasRolnaWarningMessage = !!rolnaDetaljiInfo?.message
    const isScanned = Number(formik.values.Skenirano) === 1

    return (
        <div className='card shadow-sm mb-5 mb-xl-10'>
            <ToastContainer />

            {/* Header */}
            <div className='card-header border-0 py-4'>
                <div className='d-flex flex-column'>
                    <div className='d-flex align-items-center gap-3'>
                        <TitleComponent name='Promet rolne — direktna prodaja' />
                        <span className='badge bg-light-primary text-primary fw-semibold'>Izlaz</span>
                        <span className={clsx('badge fw-semibold', isScanned ? 'bg-success' : 'bg-secondary')}>
              {isScanned ? 'Skenirano' : 'Ručni unos'}
            </span>
                    </div>
                    <div className='text-muted mt-1'>
                        Skeniraj rolnu (QR) za status <b>Skenirano</b>. Ručni unos se prikazuje kao <b>Ručni unos</b>.
                    </div>
                </div>
            </div>

            <div className='card-body pt-0'>
                {loading ? (
                    <LoadingComponent />
                ) : (
                    <form onSubmit={formik.handleSubmit} noValidate className='form'>
                        <div className='row g-6'>
                            {/* LEFT: Form fields */}
                            <div className='col-12 col-xl-7'>
                                {/* ROLNA */}
                                <div className='card border-0 bg-light mb-6'>
                                    <div className='card-body p-5'>
                                        <div className='d-flex align-items-center justify-content-between mb-3'>
                                            <div>
                                                <div className='fw-bold fs-5'>Rolna</div>
                                                <div className='text-muted small'>Skeniraj QR ili ručno unesi ID rolne.</div>
                                            </div>
                                            <span className='badge bg-light text-muted border'>Obavezno</span>
                                        </div>

                                        <div className='row g-3 align-items-stretch'>
                                            <div className='col-12 col-md-auto'>
                                                <div className='d-flex h-100 align-items-center justify-content-center border rounded bg-white px-3 py-2'>
                                                    <QrCodeScanner onScan={(data) => applyDeviceScan(String(data || ''))} />
                                                </div>
                                            </div>

                                            <div className='col'>
                                                <input
                                                    type='text'
                                                    placeholder='Upisi ili skeniraj rolnu…'
                                                    value={formik.values.Repromaterijal || ''}
                                                    ref={inputRef}
                                                    className={clsx('form-control form-control-lg', {
                                                        'is-invalid': formik.touched.Repromaterijal && formik.errors.Repromaterijal,
                                                        'is-valid': formik.touched.Repromaterijal && !formik.errors.Repromaterijal,
                                                    })}
                                                    onChange={(e) => applyManualRolnaChange(e.target.value)}
                                                    onBlur={formik.handleBlur}
                                                    name='Repromaterijal'
                                                />

                                                <div className='d-flex align-items-center justify-content-between mt-2'>
                                                    <div className='text-muted small'>
                                                        Status:{' '}
                                                        <span className={clsx('fw-semibold', isScanned ? 'text-success' : 'text-muted')}>
                              {isScanned ? 'Skenirano' : 'Ručni unos'}
                            </span>
                                                    </div>

                                                    {!!formik.values.RawScan && (
                                                        <span className='badge bg-light text-muted border'>Raw QR spremljen</span>
                                                    )}
                                                </div>

                                                {formik.touched.Repromaterijal && formik.errors.Repromaterijal && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.Repromaterijal}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* BROJ DOKUMENTA IZLAZA */}
                                <div className='card border-0 bg-light mb-6'>
                                    <div className='card-body p-5'>
                                        <div className='d-flex align-items-center justify-content-between mb-3'>
                                            <div>
                                                <div className='fw-bold fs-5'>Broj dokumenta izlaza</div>
                                                <div className='text-muted small'>Skeniraj ili upiši broj dokumenta.</div>
                                            </div>
                                            <span className='badge bg-light text-muted border'>Obavezno</span>
                                        </div>

                                        <div className='row g-3 align-items-stretch'>
                                            <div className='col-12 col-md-auto'>
                                                <div className='d-flex h-100 align-items-center justify-content-center border rounded bg-white px-3 py-2'>
                                                    <QrCodeScanner
                                                        onScan={(data) => {
                                                            const cleaned = normalizeScan(String(data || ''))
                                                            const text = cleaned.split(';')[0]
                                                            formik.setFieldValue('BrojDokumentaIzlaza', text)
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className='col'>
                                                <input
                                                    type='text'
                                                    placeholder='000005/26'
                                                    {...formik.getFieldProps('BrojDokumentaIzlaza')}
                                                    className={clsx('form-control form-control-lg', {
                                                        'is-invalid':
                                                            formik.touched.BrojDokumentaIzlaza && formik.errors.BrojDokumentaIzlaza,
                                                        'is-valid':
                                                            formik.touched.BrojDokumentaIzlaza && !formik.errors.BrojDokumentaIzlaza,
                                                    })}
                                                />
                                                {formik.touched.BrojDokumentaIzlaza && formik.errors.BrojDokumentaIzlaza && (
                                                    <div className='invalid-feedback d-block'>{formik.errors.BrojDokumentaIzlaza}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* NAPOMENE */}
                                <div className='card border-0 bg-light'>
                                    <div className='card-body p-5'>
                                        <div className='fw-bold fs-5 mb-1'>Napomena</div>
                                        <div className='text-muted small mb-3'>Opcionalno</div>

                                        <textarea
                                            rows={4}
                                            placeholder='Dodaj napomenu…'
                                            {...formik.getFieldProps('Napomene')}
                                            className={clsx('form-control form-control-lg', {
                                                'is-invalid': formik.touched.Napomene && formik.errors.Napomene,
                                                'is-valid': formik.touched.Napomene && !formik.errors.Napomene,
                                            })}
                                        />

                                        {formik.touched.Napomene && formik.errors.Napomene && (
                                            <div className='invalid-feedback d-block'>
                                                {formik.errors.Napomene as any}
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: Rolna details / status */}
                            <div className='col-12 col-xl-5'>
                                <div className='card border-0 h-100'>
                                    <div className='card-header bg-white border-0 pb-0'>
                                        <div className='d-flex align-items-center justify-content-between'>
                                            <div>
                                                <div className='fw-bold fs-5'>Detalji rolne</div>
                                                <div className='text-muted small'>Automatski se učitava nakon unosa rolne.</div>
                                            </div>

                                            <span className={clsx('badge fw-semibold', isScanned ? 'bg-success' : 'bg-secondary')}>
                        {isScanned ? 'Skenirano' : 'Ručni unos'}
                      </span>
                                        </div>
                                    </div>

                                    <div className='card-body pt-4'>
                                        {!formik.values.Repromaterijal ? (
                                            <div className='alert alert-secondary mb-0'>
                                                Unesi ili skeniraj rolnu da bi se prikazali detalji.
                                            </div>
                                        ) : rolnaLoading ? (
                                            <div className='alert alert-info mb-0'>Učitavam detalje rolne…</div>
                                        ) : rolnaError ? (
                                            <div className='alert alert-warning mb-0'>
                                                <div className='fw-bold mb-1'>Greška</div>
                                                <div>{rolnaError}</div>
                                            </div>
                                        ) : !rolnaDetaljiInfo ? (
                                            <div className='alert alert-secondary mb-0'>Nema podataka.</div>
                                        ) : hasRolnaWarningMessage ? (
                                            <div className='alert alert-warning mb-0'>
                                                <div className='fw-bold mb-1'>Poruka</div>
                                                <div>{rolnaDetaljiInfo.message}</div>
                                            </div>
                                        ) : (
                                            <div className='alert alert-success mb-0'>
                                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                                    <div className='fw-bold'>Rolna je validna</div>
                                                    <span className='badge bg-success'>OK</span>
                                                </div>

                                                <div className='border-top pt-3'>
                                                    <div className='row g-2 small'>
                                                        <div className='col-6 text-muted'>Težina</div>
                                                        <div className='col-6 fw-semibold text-end'>
                                                            {rolnaDetaljiInfo.ReprMatTrenutnaKolicina || 'N/A'}
                                                        </div>

                                                        <div className='col-6 text-muted'>Debljina</div>
                                                        <div className='col-6 fw-semibold text-end'>{rolnaDetaljiInfo.ReprMatDebljina || 'N/A'}</div>

                                                        <div className='col-6 text-muted'>Širina</div>
                                                        <div className='col-6 fw-semibold text-end'>{rolnaDetaljiInfo.ReprMatSirina || 'N/A'}</div>

                                                        <div className='col-6 text-muted'>Lokacija</div>
                                                        <div className='col-6 fw-semibold text-end'>{rolnaDetaljiInfo.ReprMatLokacija || 'N/A'}</div>

                                                        <div className='col-6 text-muted'>Red</div>
                                                        <div className='col-6 fw-semibold text-end'>{rolnaDetaljiInfo.ReprMatLokacija2 || 'N/A'}</div>

                                                        <div className='col-12'>
                                                            <hr className='my-3' />
                                                        </div>

                                                        <div className='col-6 text-muted'>Boja</div>
                                                        <div className='col-6 fw-semibold text-end'>{rolnaDetaljiInfo.boja_lica?.BojaNaziv || 'N/A'}</div>

                                                        <div className='col-6 text-muted'>Proizvođač</div>
                                                        <div className='col-6 fw-semibold text-end'>
                                                            {rolnaDetaljiInfo.proizvodjac?.PartnerNaziv || 'N/A'}
                                                        </div>

                                                        <div className='col-6 text-muted'>Šarža</div>
                                                        <div className='col-6 fw-semibold text-end'>
                                                            {/* keep your existing mapping (replace if you have correct field) */}
                                                            {rolnaDetaljiInfo.proizvodjac?.PartnerNaziv || 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Show what will be sent */}
                                        <div className='mt-4'>
                                            <div className='card border-0 bg-light'>
                                                <div className='card-body p-3'>
                                                    <div className='fw-bold mb-2'>Scan status (payload)</div>
                                                    <div className='d-flex flex-column gap-1 small'>
                                                        <div>
                                                            Skenirano:{' '}
                                                            <span className={clsx('fw-semibold', isScanned ? 'text-success' : 'text-muted')}>
                                {isScanned ? '1' : '0'}
                              </span>
                                                        </div>
                                                        <div>
                                                            ScannedByDevice:{' '}
                                                            <span className='fw-semibold'>{Number(formik.values.ScannedByDevice || 0)}</span>
                                                        </div>
                                                        <div className='text-muted text-truncate'>
                                                            RawScan: <span className='fw-semibold'>{formik.values.RawScan || '-'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer actions */}
                        <div className='card-footer bg-white border-0 d-flex justify-content-end py-5 px-0 mt-6'>
                            <button
                                type='submit'
                                className={clsx('btn btn-lg px-8', hasRolnaWarningMessage ? 'btn-secondary' : 'btn-primary')}
                                disabled={
                                    loading ||
                                    formik.isSubmitting ||
                                    !formik.isValid ||
                                    rolnaLoading ||
                                    !!rolnaError ||
                                    hasRolnaWarningMessage
                                }
                            >
                                {!loading && 'Potvrdi'}
                                {loading && (
                                    <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                                )}
                            </button>
                        </div>

                        {/* Hidden fields (optional, but keeps the formik shape consistent) */}
                        <input type='hidden' name='Skenirano' value={formik.values.Skenirano ?? 0} readOnly />
                        <input type='hidden' name='RawScan' value={formik.values.RawScan ?? ''} readOnly />
                        <input type='hidden' name='ScannedByDevice' value={formik.values.ScannedByDevice ?? 0} readOnly />
                    </form>
                )}
            </div>
        </div>
    )
}

export default PrometRolneDirektnaProdaja
